import React, { createContext, useContext, useMemo } from "react";

/**
 * Which fields are required — asked of the schema, not of the developer.
 *
 * This product kept shipping required fields with no mark on them, and the
 * reason is structural rather than careless: every field component took
 * `required` as a hand-passed prop. A prop on nine fields is nine chances to
 * forget, the forms that forgot looked no different from the forms that did
 * not, and nothing failed — so the omission survived review every time.
 *
 * It got worse than forgetting. Three field components grew three different
 * conventions: `components/ui/Input` and `components/Form` print a `*` when
 * told a field is required, while `components/Input` printed the word
 * "(optional)" on the others instead. Two opposite answers in one product, so
 * a field with no mark meant "required, and someone remembered" in one form
 * and "required, and someone forgot" in the next. A reader cannot tell those
 * apart, which makes the mark worth nothing wherever it does appear.
 *
 * The validation schema already knows. A form declares it once here and every
 * field inside asks; a field that the submit will reject cannot then be a
 * field with no mark on it, because the same object decides both.
 *
 *   <RequiredFieldsProvider schema={bankAccountSchema}>
 *     <Input name="iban" label="Account number / IBAN" />
 *   </RequiredFieldsProvider>
 *
 * A `required` prop still wins where it is passed, for the fields whose rule
 * lives somewhere other than a schema.
 */

const RequiredFieldsContext = createContext<Set<string>>(new Set());

/**
 * The names yup will refuse when empty.
 *
 * `describe()` reports each field's tests, and a `required()` call leaves a
 * test named "required" among them. Nested shapes are walked so a field
 * addressed as `howDidHear.option` resolves too.
 */
export const requiredFieldNames = (schema: any): Set<string> => {
  const names = new Set<string>();
  if (!schema?.describe) return names;

  const walk = (described: any, prefix: string) => {
    const fields = described?.fields ?? {};
    Object.keys(fields).forEach((key) => {
      const field = fields[key];
      const path = prefix ? `${prefix}.${key}` : key;
      const tests: any[] = field?.tests ?? [];
      if (tests.some((test) => test?.name === "required")) names.add(path);
      if (field?.fields) walk(field, path);
    });
  };

  try {
    walk(schema.describe(), "");
  } catch {
    /* A schema that cannot describe itself simply marks nothing, which is the
       behaviour every form had before this existed. */
  }
  return names;
};

export const RequiredFieldsProvider = ({
  schema,
  children,
}: {
  schema: any;
  children?: React.ReactNode;
}) => {
  const names = useMemo(() => requiredFieldNames(schema), [schema]);
  return (
    <RequiredFieldsContext.Provider value={names}>
      {children}
    </RequiredFieldsContext.Provider>
  );
};

/** True when the surrounding form's schema requires this field. */
export const useIsRequired = (name?: string) => {
  const names = useContext(RequiredFieldsContext);
  return name ? names.has(name) : false;
};
